/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Link } from "../components/ui/Link";
import { Download, FileText } from "lucide-react";
import {
  createReferralFromFax,
  faxDetail,
  faxPdf,
  resolveFax,
  FAX_VERDICT_LABELS,
  FAX_REFUSAL_REASONS,
  type FaxDetail,
  type FaxReviewInput,
} from "../api/faxes";
import { useAuth } from "../auth/AuthContext";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { ConfidenceRing } from "../components/ui/ConfidenceRing";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import { Textarea } from "../components/ui/Textarea";
import { FaxReviewModal } from "./FaxReviewModal";
import { ValidationError } from "../lib/validation";
import { Facts, Section } from "../components/DetailBits";
import { canWorkFaxes } from "../components/RegistryBits";

/**
 * One fax case: the PDF beside everything the pipeline extracted, the registry verdicts, and the
 * two ways to settle it. Creating uses the extraction as-is; corrections are worked in the
 * registry after creation for now.
 */
export function ReferralFaxDetail() {
  const { id } = useParams<{ id: string }>();
  const { hasRole } = useAuth();
  const { showToast } = useToast();
  const [detail, setDetail] = useState<FaxDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"create" | "resolve" | null>(null);
  const [confirming, setConfirming] = useState<"create" | "resolve" | null>(null);
  const [reviewError, setReviewError] = useState<ValidationError | null>(null);
  const [resolveNote, setResolveNote] = useState("");
  const [viewingFax, setViewingFax] = useState(false);

  useChrome({ back: { to: "/referrals/faxes", label: "Fax inbox" } }, []);

  const refresh = useCallback(async () => {
    if (!id) {
      return;
    }
    const result = await faxDetail(id);
    if (result.ok) {
      setDetail(result.value);
      setError(null);
    } else {
      setError(result.error.message);
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function act(kind: "create" | "resolve", review: FaxReviewInput | null) {
    if (!id) {
      return;
    }
    setBusy(kind);
    setReviewError(null);
    const result =
      kind === "create" && review !== null
        ? await createReferralFromFax(id, review)
        : await resolveFax(id, resolveNote.trim());
    setBusy(null);
    if (!result.ok) {
      if (kind === "create") {
        setReviewError(ValidationError.fromApiError(result.error));
      } else {
        setConfirming(null);
        showToast("error", result.error.message);
      }
      return;
    }
    setConfirming(null);
    setDetail(result.value);
    showToast(
      "success",
      kind === "create"
        ? `Referral ${result.value.summary.referralId} created.`
        : "Case marked resolved.",
    );
  }

  if (detail === null && error === null) {
    return (
      <div className="grid place-items-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }
  if (detail === null) {
    return <ErrorMessage message={error} />;
  }

  const { summary, extraction } = detail;
  const canAct = canWorkFaxes(hasRole) && summary.status === "PENDING";
  const pending = summary.status === "PENDING";

  return (
    <div className="space-y-8">
      <header className="enter flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          {summary.confidence !== null && (
            <ConfidenceRing
              value={summary.confidence}
              hint={(pct) =>
                `The intake agent was ${pct}% sure of what it read on this fax. Above the threshold it creates the referral itself; below, it leaves the fax for your review.`
              }
            />
          )}
          <div>
            <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
              Fax from {summary.fromNumber ?? "unknown sender"}
            </h1>
            <p className="mt-2 flex flex-wrap items-center gap-2 text-[15px] text-text-secondary">
              <Badge tone={pending ? "warning" : "success"}>
                {pending ? "Pending" : "Resolved"}
              </Badge>
              {summary.resolution && <Badge tone="neutral">{resolutionLabel(summary.resolution)}</Badge>}
              {summary.referralId !== null && (
                <Link
                  className="text-sm text-accent underline-offset-2 hover:underline"
                  to={`/referrals/${summary.referralId}`}
                >
                  Referral {summary.referralId}
                </Link>
              )}
              {detail.reviewInput && <Badge tone="info">Reviewed before creation</Badge>}
            </p>
            {detail.resolutionNote && (
              <p className="mt-2 text-sm text-text-secondary">
                Resolved because: {detail.resolutionNote}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="md" onClick={() => setViewingFax(true)}>
            <FileText size={16} className="mr-1.5" />
            View fax
          </Button>
          {canAct && (
            <>
              <Button
                variant="primary"
                size="md"
                disabled={busy !== null || extraction === null}
                onClick={() => setConfirming("create")}
              >
                Create referral
              </Button>
              <Button
                variant="secondary"
                size="md"
                disabled={busy !== null}
                onClick={() => setConfirming("resolve")}
              >
                {busy === "resolve" ? "Resolving…" : "Mark resolved"}
              </Button>
            </>
          )}
        </div>
      </header>

      {pending && summary.autoCreateRefusal !== null && detail.intakeError === null && (
        <div className="mb-6 rounded-2xl border border-warning-border bg-warning-bg px-4 py-3">
          <p className="text-sm font-medium text-text-primary">Held for review</p>
          <p className="mt-1 text-sm leading-relaxed text-text-secondary">
            {FAX_REFUSAL_REASONS[summary.autoCreateRefusal] ?? summary.autoCreateRefusal}
          </p>
        </div>
      )}

      {detail.intakeError && (
        <ErrorMessage
          message={`Intake could not finish: ${detail.intakeError}. Review the fax in Healthie or retry from there.`}
        />
      )}

      <div className="grid gap-6 md:grid-cols-2">
          {extraction === null ? (
            <Section title="Extraction" className="md:col-span-2">
              <p className="text-sm text-text-tertiary">
                Nothing was extracted from this fax. Work it from the PDF and mark it resolved.
              </p>
            </Section>
          ) : (
            <>
              <Section title="Patient">
                <Facts
                  facts={[
                    ["Name", joinName(extraction.patient?.firstName, extraction.patient?.lastName)],
                    ["Date of birth", extraction.patient?.dateOfBirth ?? "—"],
                    ["State", extraction.patient?.stateCode ?? "—"],
                  ]}
                />
              </Section>
              <Section title="Guardian">
                <Facts
                  facts={[
                    ["Name", joinName(extraction.guardian?.firstName, extraction.guardian?.lastName)],
                    ["Phone", extraction.guardian?.phone ?? "—"],
                    ["Email", extraction.guardian?.email ?? "—"],
                  ]}
                />
              </Section>
              <Section title="Referring provider">
                <VerdictLine
                  verdict={summary.providerVerdict}
                  matchedId={detail.providerId}
                  matchedTo="/referrals/providers/"
                  candidates={detail.providerCandidates}
                />
                <Facts
                  facts={[
                    [
                      "Name",
                      joinName(
                        extraction.referringProvider?.firstName,
                        extraction.referringProvider?.lastName,
                      ),
                    ],
                    ["NPI", extraction.referringProvider?.npi ?? "—"],
                    ["Email", extraction.referringProvider?.email ?? "—"],
                    ["Fax", extraction.referringProvider?.faxNumber ?? "—"],
                  ]}
                />
              </Section>
              <Section title="Referring organization">
                <VerdictLine
                  verdict={summary.organizationVerdict}
                  matchedId={detail.organizationId}
                  matchedTo="/referrals/organizations/"
                  candidates={detail.organizationCandidates}
                />
                <Facts
                  facts={[
                    ["Name", extraction.referringOrganization?.name ?? "—"],
                    ["State", extraction.referringOrganization?.stateCode ?? "—"],
                    ["Email domain", extraction.referringOrganization?.emailDomain ?? "—"],
                  ]}
                />
              </Section>
              <Section title="Referral">
                <Facts
                  facts={[
                    ["Services requested", extraction.referral?.servicesRequested?.join(", ") || "—"],
                    ["Reason", extraction.referral?.reasonForReferral ?? "—"],
                    ["Notes", extraction.referral?.notes ?? "—"],
                  ]}
                />
              </Section>
              <Section title="Insurance">
                <Facts
                  facts={[
                    ["Payer", extraction.insurance?.payerName ?? "—"],
                    ["Subscriber ID", extraction.insurance?.subscriberId ?? "—"],
                  ]}
                />
              </Section>
            </>
          )}
      </div>

      {viewingFax && (
        <FaxModal id={summary.id} fromNumber={summary.fromNumber} onClose={() => setViewingFax(false)} />
      )}

      {confirming === "create" && (
        <FaxReviewModal
          detail={detail}
          busy={busy !== null}
          error={reviewError}
          onClose={() => {
            setConfirming(null);
            setReviewError(null);
          }}
          onConfirm={(review) => void act("create", review)}
        />
      )}

      {confirming === "resolve" && (
        <Modal isOpen title="Mark this fax resolved?" size="sm" onClose={() => setConfirming(null)}>
          <div className="space-y-4 px-6 py-5">
            <p className="text-sm leading-relaxed text-text-secondary">
              The case closes without creating a referral. Say why, so the next person to open it
              knows: not a referral, a duplicate, handled directly in Healthie.
            </p>
            <Textarea
              id="resolve-note"
              label="Reason"
              value={resolveNote}
              onChange={(event) => setResolveNote(event.target.value)}
              placeholder="Why this fax needs no referral"
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setConfirming(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={busy !== null || resolveNote.trim() === ""}
                onClick={() => void act("resolve", null)}
              >
                {busy === "resolve" ? "Resolving…" : "Mark resolved"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * The fax PDF on demand: fetched through the authenticated client only when the modal opens (a
 * bare object/iframe URL carries no session headers and 401s) and shown in the browser's viewer
 * from an object URL that is revoked, and the fetch aborted, when the modal closes.
 */
function FaxModal({
  id,
  fromNumber,
  onClose,
}: {
  id: string;
  fromNumber: string | null;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setUrl(null);
    setFailure(null);
    void faxPdf(id, controller.signal).then((result) => {
      if (controller.signal.aborted) {
        return;
      }
      if (!result.ok) {
        setFailure(result.error.message);
        return;
      }
      objectUrl = URL.createObjectURL(result.value);
      setUrl(objectUrl);
    });
    return () => {
      controller.abort();
      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [id, attempt]);

  return (
    <Modal isOpen title={`Fax from ${fromNumber ?? "unknown sender"}`} size="full" onClose={onClose}>
      <div className="space-y-3 px-6 py-5">
        {failure !== null ? (
          <div className="space-y-3">
            <ErrorMessage message={`The fax could not be loaded: ${failure}`} />
            <Button variant="secondary" size="sm" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </Button>
          </div>
        ) : url === null ? (
          <div className="grid h-[75vh] place-items-center rounded-xl bg-bg-elevated">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            <iframe
              src={url}
              title="Fax PDF"
              className="h-[75vh] w-full rounded-xl border border-border bg-bg-elevated"
            />
            <a
              className="inline-flex items-center gap-1.5 text-sm text-accent underline-offset-2 hover:underline"
              href={url}
              download={`fax-${fromNumber ?? id}.pdf`}
            >
              <Download size={14} />
              Download PDF
            </a>
          </>
        )}
      </div>
    </Modal>
  );
}

function VerdictLine({
  verdict,
  matchedId,
  matchedTo,
  candidates,
}: {
  verdict: string | null;
  matchedId: number | null;
  matchedTo: string;
  candidates: Record<string, unknown>[] | null;
}) {
  if (!verdict) {
    return null;
  }
  return (
    <p className="mb-3 flex flex-wrap items-center gap-2 text-sm text-text-secondary">
      <Badge tone={verdict === "MATCH" ? "success" : verdict === "AMBIGUOUS" ? "warning" : "info"}>
        {FAX_VERDICT_LABELS[verdict] ?? verdict}
      </Badge>
      {matchedId !== null && (
        <Link className="text-accent underline-offset-2 hover:underline" to={`${matchedTo}${matchedId}`}>
          View matched record
        </Link>
      )}
      {verdict === "AMBIGUOUS" && candidates && candidates.length > 0 && (
        <span className="text-text-tertiary">
          {candidates.length === 1 ? "1 candidate" : `${candidates.length} candidates`} considered
        </span>
      )}
    </p>
  );
}

function joinName(first: string | null | undefined, last: string | null | undefined): string {
  const joined = [first, last].filter(Boolean).join(" ");
  return joined || "—";
}

function resolutionLabel(resolution: string): string {
  switch (resolution) {
    case "AUTO_CREATED":
      return "Auto-processed";
    case "REFERRAL_CREATED":
      return "Referral created";
    case "MANUALLY_RESOLVED":
      return "Manually resolved";
    default:
      return resolution;
  }
}
